// SunControls.ts
import * as THREE from "three";
import { spa_calculate } from "./NREL";
import { GetDateVal, GetRadVal, GetRoundNum } from "../Controllers/Common";
import { useViewerStore } from "../../../../../store/store";

// ---- constants ----
export const radiusOut = 120;
export const radiusSun = 5;

export interface SunInfo {
    date: Date;
    lat: number;
    lon: number;
    zone: number;
    time: number;
    elevation: number;
    utcOffset ?: number;
    timezone ?: number;
}

export const labelMonthArr = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
] as const;

export interface BuildingAlt {
    low: number;
    high: number;
}

export interface ControllerSelf {
    sunInfo: SunInfo;
    buildingAlt?: BuildingAlt | null;
    sunGroup: THREE.Group;
    roofGroup: THREE.Group;
    sunVecArr?: SunVecArr;
}

export interface TimeInfo {
    year: number;
    month: number; // 1-12
    day: number;   // 1-31
    hour: number;  // 0-23
    min: number;   // 0-59
}

export interface SunVec {
    x: number;
    y: number;
    z: number;
}

// sunVecArr[month][day][hour] -> SunVec
export type SunVecArr = SunVec[][][];

// ---- helpers ----
function dayOfYearToDate(dayOfYear: number): string {
    const currentYear = new Date().getFullYear();
    const date = new Date(currentYear, 0, dayOfYear);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${month}-${day}-${year}`;
}

export type SunGroupWorldTransform = {
    offsetX: number;
    offsetZ: number;
    scaleZ: number;
};


export const LEGACY_SUN_GROUP_TRANSFORM: SunGroupWorldTransform = {
    offsetX: 0,
    offsetZ: 0,
    scaleZ: -1,
};

/** Offset visual sun path/sphere to roof AABB center. sunVecArr stays origin-centered; shade raycasting applies this transform. */
export function PositionSunGroupToDesignCenter(self: {
    sunGroup: THREE.Group;
    roofGroup: THREE.Group;
}): void {
    const useDesignCenteredSun = useViewerStore.getState().useDesignCenteredSunPosition;
    if (!useDesignCenteredSun || !self.roofGroup?.children?.length) {
        self.sunGroup.position.set(0, 0, 0);
        return;
    }
    const center = new THREE.Box3()
        .setFromObject(self.roofGroup)
        .getCenter(new THREE.Vector3());
    self.sunGroup.position.set(center.x, 0, center.z);
}

export function getSunGroupWorldTransform(sunGroup: THREE.Group): SunGroupWorldTransform {
    return {
        offsetX: sunGroup.position.x,
        offsetZ: sunGroup.position.z,
        scaleZ: sunGroup.scale.z,
    };
}


export function getShadeSunGroupTransform(sunGroup: THREE.Group): SunGroupWorldTransform {
    if (!useViewerStore.getState().useDesignCenteredSunPosition) {
        return { ...LEGACY_SUN_GROUP_TRANSFORM };
    }
    return getSunGroupWorldTransform(sunGroup);
}


export function sunVecLocalToWorld(vec: SunVec, transform: SunGroupWorldTransform): SunVec {
    return {
        x: vec.x + transform.offsetX,
        y: vec.y,
        z: (-vec.z) * transform.scaleZ + transform.offsetZ,
    };
}

// ---- API ----
export function LoadSunGroup(sunGroup: THREE.Group): void {
    const sunGeo = new THREE.SphereGeometry(radiusSun, 32, 32);
    const sunMat = new THREE.MeshStandardMaterial({
        color: 0xfdb813,
        emissive: 0xfdb813,
    });
    const sunMesh = new THREE.Mesh(sunGeo, sunMat);
    const sunLight = new THREE.PointLight(0xffffff, 1);

    sunLight.name = "sunLight";
    sunLight.add(sunMesh);
    sunLight.castShadow = true;
    sunLight.shadow.mapSize.width = 1024;
    sunLight.shadow.mapSize.height = 1024;

    sunGroup.add(sunLight);
    // mirror Z for your coordinate convention
    sunGroup.scale.z = -1;
}

export function SetSunVecArr(self: ControllerSelf): void {
    const { sunInfo, buildingAlt } = self;
    if (!sunInfo?.utcOffset || !buildingAlt || buildingAlt.low === 0.1) return;

    const { low, high } = buildingAlt;
    const elevation = (low + high) / 2;
    sunInfo.elevation = elevation;

    self.sunVecArr = CreateSunVecArr(sunInfo);
    SetSunPathGeo(self.sunGroup, self.sunVecArr, sunInfo);
    PositionSunGroupToDesignCenter(self);
}

export function CreateSunVecArr(sunInfo: SunInfo): SunVecArr {
    const year = new Date().getFullYear();
    const sunVecArr: SunVecArr = [];
    const dateFirst = new Date(year, 0, 1);
    const countDecimal = 5;

    for (let i = 1; i < 366; i++) {
        const dateNew = new Date(dateFirst);
        dateNew.setDate(i);
        sunInfo.date = GetDateVal(dateNew) as Date;

        const day = dateNew.getDate() - 1;
        const month = dateNew.getMonth();
        if (!sunVecArr[month]) sunVecArr[month] = [];

        sunVecArr[month][day] = [];
        for (let j = 0; j < 24; j++) {
            sunInfo.time = j;
            const timeInfo: TimeInfo = {
                year,
                month: month + 1,
                day: day + 1,
                hour: j === 23 ? 0 : j + 1,
                min: 55,
            };
            const { x, y, z } = SetSunPos(null, sunInfo, timeInfo, "init");
            sunVecArr[month][day].push({
                x: GetRoundNum(x, countDecimal),
                y: GetRoundNum(y, countDecimal),
                z: GetRoundNum(z, countDecimal),
            });
        }
    }

    return sunVecArr;
}

export function SetSunPathGeo(
    sunGroup: THREE.Group,
    sunVecArr: SunVecArr,
    info: SunInfo
): void {
    const oldSunPathMesh = sunGroup.children.find(
        (child) => (child as THREE.Object3D).name === "sunPath"
);
    if (oldSunPathMesh) {
        sunGroup.remove(oldSunPathMesh);
    }

    const sunPathGeo = new THREE.BufferGeometry();
    const sunPathMat = new THREE.LineBasicMaterial({ color: 0x3250b4 });

    const mainVerArr: number[] = [];
    const analemmaMat = new THREE.LineDashedMaterial({
        color: 0xff9b75,
        linewidth: 0.5, // note: linewidth is mostly ignored by WebGL implementations
    });

    const analemmaLineArr: THREE.LineLoop[] = [];

    const { date } = info;
    const year = new Date().getFullYear();

    for (let t = 0; t < 24; t++) {
        const timeVal = new Date(date);
        timeVal.setHours(t, 0, 0, 0);

        const month = timeVal.getMonth();
        const day = timeVal.getDate() - 1;

        const vector = sunVecArr?.[month]?.[day]?.[t];
        if (!vector) continue;

        // Negate Z to match sun sphere local position under sunGroup.scale.z = -1
        mainVerArr.push(vector.x, vector.y, -vector.z);

        // Build analemma line (all days for fixed hour)
        const analemmaGeo = new THREE.BufferGeometry();
        const vertices: number[] = [];

        for (let i = 1; i < 366; i++) {
            const subTimeVal = new Date(year, 0, i);
            const subMonth = subTimeVal.getMonth();
            const subDay = subTimeVal.getDate() - 1;

            const v = sunVecArr?.[subMonth]?.[subDay]?.[t];
            if (!v) continue;
            vertices.push(v.x, v.y, -v.z);
        }

        if (vertices.length < 3) continue;

        analemmaGeo.setAttribute(
            "position",
            new THREE.BufferAttribute(new Float32Array(vertices), 3)
        );
        const analemmaLine = new THREE.LineLoop(analemmaGeo, analemmaMat);
        analemmaLine.computeLineDistances();
        analemmaLine.name = `analemma-${t}`;
        analemmaLine.add(createAnalemmaHourLabel(t, getAnalemmaLabelPosition(vertices)));
        analemmaLineArr.push(analemmaLine);
    }

    sunPathGeo.setAttribute(
        "position",
        new THREE.BufferAttribute(new Float32Array(mainVerArr), 3)
    );

    const sunPathMesh = new THREE.LineLoop(sunPathGeo, sunPathMat);
    sunPathMesh.visible = false;
    sunPathMesh.name = "sunPath";

    analemmaLineArr.forEach((line) => {
        sunPathMesh.add(line);
    });

    sunGroup.add(sunPathMesh);

    const labelSunDate = document.getElementById("sunDate");
    if (labelSunDate) {
        labelSunDate.textContent =
            "Select Date:" +
            date.getDate() +
            " / " +
            labelMonthArr[date.getMonth()] +
            " / " +
            date.getFullYear();
    }
}

function formatAnalemmaHourLabel(hour: number): string {
    const h12 = hour % 12 || 12;
    const ampm = hour < 12 ? "AM" : "PM";
    return `${h12} ${ampm}`;
}

/** Place label at the highest point on the analemma, nudged slightly outward. */
function getAnalemmaLabelPosition(vertices: number[]): THREE.Vector3 {
    let bestX = vertices[0];
    let bestY = vertices[1];
    let bestZ = vertices[2];
    for (let i = 3; i < vertices.length; i += 3) {
        if (vertices[i + 1] > bestY) {
            bestX = vertices[i];
            bestY = vertices[i + 1];
            bestZ = vertices[i + 2];
        }
    }
    const pos = new THREE.Vector3(bestX, bestY, bestZ);
    if (pos.lengthSq() > 0) {
        pos.multiplyScalar(1.08);
    }
    return pos;
}

/**
 * WebGL sprite labels — CSS2D becomes invisible under sunGroup.scale.z = -1.
 * Negative scale.x undoes the mirror from the parent reflection so text stays readable.
 */
function createAnalemmaHourLabel(hour: number, position: THREE.Vector3): THREE.Sprite {
    const canvas = document.createElement("canvas");
    canvas.width = 160;
    canvas.height = 64;
    const ctx = canvas.getContext("2d");
    if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = "rgba(0, 0, 0, 0.65)";
        ctx.fillRect(4, 8, canvas.width - 8, canvas.height - 16);
        ctx.fillStyle = "#ff9b75";
        ctx.font = "bold 28px Helvetica, Arial, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(formatAnalemmaHourLabel(hour), canvas.width / 2, canvas.height / 2);
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    texture.minFilter = THREE.LinearFilter;

    const material = new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        depthTest: false,
        depthWrite: false,
        sizeAttenuation: true,
    });

    const sprite = new THREE.Sprite(material);
    // Flip X to counteract sunGroup.scale.z = -1 mirror
    sprite.scale.set(-14, 5.5, 1);
    sprite.position.copy(position);
    sprite.center.set(0.5, 0.5);
    sprite.renderOrder = 9999;
    sprite.name = `analemmaHour-${hour}`;
    return sprite;
}

// ---- SetSunPos overloads ----
export function SetSunPos(
    sunGroup: null,
    info: SunInfo,
    timeInfo: TimeInfo,
    type: "init"
): SunVec;
export function SetSunPos(
    sunGroup: THREE.Group,
    info: SunInfo,
    timeInfo: TimeInfo,
    type?: undefined
): void;
export function SetSunPos(
    sunGroup: THREE.Group | null,
    info: SunInfo,
    timeInfo: TimeInfo,
    type?: "init"
): SunVec | void {
    // SPA-based computation
    const axisDir = 1;
    const { utcOffset, lat, lon, elevation = 0 } = info;
    const { year, month, day, hour, min } = timeInfo;

    const spa = {
        year,
        month,
        day,
        hour,
        min,
        timezone: utcOffset ?? 0,
        longitude: lon,
        latitude: lat,
        elevation,
        pressure: 1013,
        temperature: 11,
        slope: 0,
        azm_rotation: 0,
        atmos_refract: 0.5667,
        delta_ut1: 0,
        delta_t: 64.797,
    };

    const { d_azimuth, d_zenith }: { d_azimuth: number; d_zenith: number } =
        spa_calculate(spa);

    const azimuth = GetRadVal(d_azimuth) as number;
    const altitude = GetRadVal(90 - d_zenith) as number;

    const x = radiusOut * Math.cos(altitude) * Math.sin(azimuth) * axisDir;
    const z = radiusOut * Math.cos(altitude) * Math.cos(azimuth) * axisDir * -1;
    const y = radiusOut * Math.sin(altitude);

    if (type === "init") {
        return { x, y, z };
    }

    if (sunGroup) {
        const sunLight = sunGroup.children.find(
            (child) => (child as THREE.Object3D).name === "sunLight"
    ) as THREE.PointLight | undefined;

        if (sunLight) {
            sunLight.position.set(x, y, -z);
        }

        const labelSunTime = document.getElementById("labelSunTime");
        // format to 12h with leading zeros
        let hh = hour;
        let mm = min;
        const ampm = hh >= 12 ? "pm" : "am";
        hh = hh % 12;
        hh = hh ? hh : 12;
        const hhStr = (hh < 10 ? "0" : "") + hh.toString();
        const mmStr = (mm < 10 ? "0" : "") + mm.toString();

        if (labelSunTime) {
            labelSunTime.textContent = `Select Time : ${hhStr} : ${mmStr} ${ampm}`;
        }

        const sunDateFromStore = (useViewerStore.getState() as any).sunDate as number; // day-of-year?
        const dateToDisplay = dayOfYearToDate(sunDateFromStore);

        const labelSunDate = document.getElementById("sunDate");
        if (labelSunDate) {
            labelSunDate.textContent = `Select Date : ${dateToDisplay}`;
        }
    }
}

export function SetSunGroupPos(
    sunGroup: THREE.Group,
    sunInfo: SunInfo
): void {
    const { date, time } = sunInfo;
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hour = Math.floor(time);
    const min = Math.floor((time - hour) * 60);

    const timeInfo: TimeInfo = { year, month, day, hour, min };
    SetSunPos(sunGroup, sunInfo, timeInfo);
}

export function CheckSunTimePlay(self: ControllerSelf): void {
    const btnSunTimePlay = document.getElementsByClassName("btn-sun-time-play")[0];
    const rangeSunTimeEl = document.getElementById("rangeSunTime") as
    | HTMLInputElement
    | null;

    if (!btnSunTimePlay || !rangeSunTimeEl) return;
    const active = btnSunTimePlay.classList.contains("active");
    if (!active) return;

    let newTime = self.sunInfo.time + 1 / 60;
    if (newTime >= 24) newTime = 0;

    self.sunInfo.time = newTime;
    rangeSunTimeEl.value = String(newTime);

    SetSunGroupPos(self.sunGroup, self.sunInfo);
}
